import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, H3, Hero, In, JsonLd, List, P, Related, Src, Steps, WhatIs, articleLd } from "@/components/seo/kit";

// /guides/auto-reply-to-common-customer-questions. Plan: gtm/products/clearmakes-inbox/seo/pages/auto-reply-to-common-customer-questions.md
// A how-to guide with free reply templates (the unique part). The templates are ours to copy.

const PATH = "/guides/auto-reply-to-common-customer-questions";
const TITLE = "Auto-Reply to Common Customer Questions (8 Templates)";
const DESC = "How to auto-reply to the customer questions you answer every day: find them, write one good answer each, then send them automatically. 8 free templates.";

// Written for this page. Words in {curly brackets} are yours to fill in.
const TEMPLATES: { q: string; a: string }[] = [
  {
    q: "Where is my order?",
    a: "Hi {first name}, thanks for checking in. Orders ship within {1–2} working days, and you get a tracking link by email as soon as yours leaves us. If it has been longer than that, reply with your order number and we'll look it up today.",
  },
  {
    q: "What are your prices?",
    a: "Hi {first name}, our prices are all on one page: {pricing link}. In short, {plan or product} costs {price}, and {what is included}. If you tell me a little about what you need, I can point you to the right option.",
  },
  {
    q: "Do you offer refunds?",
    a: "Hi {first name}, yes. You can ask for a refund within {14} days of buying, and the full policy is here: {refund link}. Reply with your order number and the reason, and a person on our team will take it from there.",
  },
  {
    q: "How long does delivery take?",
    a: "Hi {first name}, delivery usually takes {3–5} working days within {country} and {7–10} days elsewhere. You'll get a tracking link by email when your order ships.",
  },
  {
    q: "Can I book a call?",
    a: "Hi {first name}, of course. Pick any time that suits you here: {booking link}. If none of the times work, reply with two or three that do and we'll make one of them work.",
  },
  {
    q: "What are your opening hours?",
    a: "Hi {first name}, we're open {Monday to Friday, 9am to 5pm} ({time zone}). Messages that arrive outside those hours are answered the next working morning.",
  },
  {
    q: "Do you ship to {country}?",
    a: "Hi {first name}, yes, we ship to {list of countries}. Shipping to {country} costs {price} and takes about {days} working days. Any import taxes are shown at checkout.",
  },
  {
    q: "How do I change or cancel my order?",
    a: "Hi {first name}, we can change or cancel an order until it ships, usually within {hours} hours. Reply with your order number and what you'd like changed, and we'll confirm by email.",
  },
];

const faqs = [
  {
    q: "Which customer questions should never get an automatic reply?",
    a: "Anything that needs a decision or judgement: complaints, refund disputes, legal or medical questions, and anything from an upset customer. Answer those yourself. Automate the answers that are the same every time, such as opening hours, prices and delivery times.",
  },
  {
    q: "Is an out-of-office reply the same thing?",
    a: "No. An out-of-office sends the same message to everyone. An automatic answer to a common question only goes to people who asked that question, and it answers it.",
  },
  {
    q: "How many saved answers do I need?",
    a: "Start with the 5 to 10 questions you answer most, because those save the most typing. Add more only when the same new question keeps coming back.",
  },
  {
    q: "Can I try automatic answers for free?",
    a: "Yes. ClearMakes Inbox's free plan includes 1 Smart FAQ answer and 25 replies a month, so you can test your most common question. Starter ($29 a month) keeps 25 answers and sends 500 replies a month.",
  },
];

const Guide = () => (
  <>
    <PageMeta
      title={TITLE}
      description={DESC}
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Guides", path: "/guides" },
        { name: "Auto-reply to common customer questions", path: PATH },
      ]}
    />
    <JsonLd data={articleLd(TITLE, DESC, PATH)} />
    <main>
      <Hero
        eyebrow="Guide · Customer email"
        title="How to auto-reply to common customer questions"
        lead="Find the questions you answer every day, write one good answer for each, then let them go out on their own. With 8 templates you can copy."
        primary={null}
        secondary={{ label: "Jump to the templates", href: "#templates" }}
      />

      <WhatIs title="What is an automatic reply to a common question?">
        An automatic reply to a common question is a saved answer that goes out when a customer asks something you have answered many times,
        such as “where is my order?” or “do you ship to Canada?”. Unlike an out-of-office message, it only goes to people who asked that
        question, and it answers it.
      </WhatIs>

      <Band
        tone="pale"
        title="Step 1: find the questions you answer every day"
        answer="Look at what you actually send, not what you think you send. Twenty minutes in your sent folder usually shows the same five to ten questions again and again."
      >
        <Steps
          items={[
            { title: "Search your sent mail for the last month", body: "Search for words like “order”, “price”, “refund”, “delivery”, “hours” and “call”." },
            { title: "Write each question down once, in the customer's words", body: "“Where's my parcel?” and “has my order shipped?” are the same question." },
            { title: "Count them and keep the top ten", body: "Those are worth a saved answer. Rare questions are not." },
          ]}
        />
        <P>
          One shop owner put the problem simply: “80% of them are the same questions over and over.” (From a{" "}
          <Src href="https://community.shopify.com/t/drowning-in-wheres-my-order-emails-would-you-use-an-ai-that-drafts-responses-for-you-to-approve/575044">Shopify Community thread</Src>, read 29 September 2026.)
        </P>
      </Band>

      <Band
        tone="white"
        id="templates"
        title="Step 2: write one good answer for each (8 templates)"
        answer="A good saved answer answers the question in the first sentence, gives the one link or number they need, and says what to do if it didn't help. Copy these and change the words in {curly brackets}."
      >
        <ul className="grid gap-4 lg:grid-cols-2">
          {TEMPLATES.map((t) => (
            <li key={t.q} className="rounded-2xl border border-[hsl(216_40%_84%)] bg-[hsl(216_60%_98%)] p-5 sm:p-6">
              <h3 className="text-[1.05rem] font-semibold text-[hsl(0_0%_4%)]">“{t.q}”</h3>
              <p className="mt-3 whitespace-pre-line text-[15.5px] leading-relaxed text-[hsl(215_16%_26%)]">{t.a}</p>
            </li>
          ))}
        </ul>
        <P>You are free to copy and change these templates for your own replies.</P>
      </Band>

      <Band
        tone="pale"
        title="Step 3: send them faster by hand first"
        answer="Before you automate anything, save the answers as templates in your email app and use them for a week. You will fix the wording that confuses people, and you will learn which answers are safe to send without looking."
        narrow
      >
        <List
          items={[
            <>
              Gmail: turn on Templates in Settings, “See all settings”, Advanced; then in a new message choose More, Templates, “Save draft as template”. Source:{" "}
              <Src href="https://support.google.com/a/users/answer/9308990">Google Workspace help, Gmail templates</Src>, checked 29 September 2026.
            </>,
            "Outlook: save each answer as a template or a signature-style snippet in Outlook's own settings.",
            "Keep a note of any answer you had to change for a customer. Those are not ready to send by themselves yet.",
          ]}
        />
      </Band>

      <Band
        tone="white"
        title="Step 4: let the safe ones go out automatically"
        answer="Email rules can sort and forward, but they can't tell what a customer asked, so they can't pick the right answer. For that you need a tool that reads the question and matches it to your saved answers."
      >
        <H3>With ClearMakes Inbox Smart FAQ</H3>
        <Steps
          items={[
            { title: "Connect your mailbox", body: <>Connect Gmail or Outlook in <In href="/products/inbox">ClearMakes Inbox</In>.</> },
            { title: "Add your saved answers to Smart FAQ", body: "One question and the answer you would really send. Start with your top three." },
            { title: "Let it answer, then read what it sent", body: "When a new email asks one of those questions, Inbox replies with your answer. Every reply it sends is listed, so you can check them." },
          ]}
        />
        <P>
          The free plan keeps 1 saved answer and sends 25 replies a month; Starter keeps 25 answers and sends 500 replies a month for $29.
          See <In href="/smart-faq">how Smart FAQ answers common questions</In>.
        </P>
      </Band>

      <Band tone="pale" title="Rules that keep automatic answers safe" answer="Automate the answer, never the decision. If a reply could cost money, upset someone or make a promise, a person should send it." narrow>
        <List
          items={[
            "Only automate answers that are the same every time.",
            "Never auto-answer complaints, refund disputes, or anything from someone who sounds upset.",
            "Say how to reach a person at the end of every automatic answer.",
            "Read a sample of what went out every week, and fix answers that caused a second email.",
            "Change the answer the day a price, policy or delivery time changes.",
          ]}
        />
        <P>
          The rule comes from a shop owner who stopped drowning in “where's my order?” emails: “automate the answers, not the decisions.” (
          <Src href="https://community.shopify.com/t/how-we-stopped-drowning-in-wheres-my-order-tickets-without-a-bot-making-promises-it-shouldnt/638971">Shopify Community</Src>, read 29 September 2026.)
        </P>
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/smart-faq", label: "Smart FAQ", desc: "Automatic answers to everyday questions from your saved answers." },
          { href: "/for-freelancers", label: "For freelancers", desc: "Answer common client questions without typing them again." },
          { href: "/lead-response-automation", label: "Lead response automation", desc: "Reply to new enquiries in minutes." },
          { href: "/guides", label: "All guides", desc: "More step-by-step guides for email and chat work." },
        ]}
      />

      <Closing title="Let your saved answers go out on their own." line="Connect your mailbox, add your top three answers, and read what gets sent." />
    </main>
    <Footer />
  </>
);

export default Guide;
